import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as yup from "yup";
import { en, de } from "yup-locales";
import { getLang } from "store/userSlice";
import { fetchInterviewsSettings, getComplaintEnabled, getAllInterviewsSettings } from "store/interviewsSettingsSlice";
import { api } from "helpers/axios";
import { errorMessage, formatDateTime } from "helpers/common";
import { toast } from "helpers/toast";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { useOutletContext } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { helpTextIcon } from "helpers/helpText";
import { Button, DateInput, Field, Input } from "components/ui";

dayjs.extend(customParseFormat);

export default function Interview() {
  const dispatch = useDispatch();
  const { helps } = useOutletContext();
  const { translate, editableTranslate } = useTranslation();
  const lang = useSelector(getLang);

  const interviews = useSelector(getAllInterviewsSettings);
  const complaintEnabled = useSelector(getComplaintEnabled);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const schema = yup.object().shape({
    interview_settings: yup.array().of(
      yup.object().shape({
        hash_id: yup.string().required(),
        is_active: yup.boolean().default(false),
        is_mandatory: yup.boolean().default(false),
        name: yup.string().required().trim().max(255).label(translate('interview.interview_name', 'Gesprächsart')),
        due_date: yup.string().nullable().optional()
          .test('valid-date', translate('form.valid_format_date', 'Gültiges Format:DD.MM.YYYY'), (val) =>
            !val || val.trim() === '' || dayjs(val, 'DD.MM.YYYY', true).isValid()),
        min_length: yup.number().nullable()
          .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
          .when('is_active', {
            is: true,
            then: (schema) =>
              schema.typeError(translate('form.valid_numeric', 'Muss eine Zahl sein.'))
                .integer().min(0).max(255).required()
                .label(translate('interview.interview_min_characters', 'minimale Zeichenlänge')),
            otherwise: (schema) => schema.nullable().optional()
          })
      })
    ),
    complaint_enable: yup.boolean().default(false)
  });

  const { register, reset, watch, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      interview_settings: [],
      complaint_enable: false
    }
  });

  const watchedSettings = watch('interview_settings');

  useEffect(() => {
    dispatch(fetchInterviewsSettings());
  }, [dispatch]);

  useEffect(() => {
    if (interviews) {
      reset({
        interview_settings: interviews.map((f) => ({
          hash_id: f.hash_id,
          is_active: Boolean(f.is_active),
          is_mandatory: Boolean(f.is_mandatory),
          name: f.name,
          due_date: f.due_date ? formatDateTime(f.due_date, true, false) : null,
          min_length: f?.min_length
        })),
        complaint_enable: Boolean(complaintEnabled)
      });
    }
  }, [interviews, complaintEnabled, reset]);

  const onSubmit = async (values) => {
    setError(null);
    setLoading(true);

    try {
      const { status } = await api.post('/settings/interviews', values);

      if (status === 200) {
        toast.success(translate('form.success.saved'));
        dispatch(fetchInterviewsSettings());
      }
    } catch (err) {
      setError(errorMessage(err, 'save interview settings'));
      Sentry.captureException(err);
    }

    setLoading(false);
  };

  const getLabel = (key, translation_key, fallback) => {
    const sKey = key.replaceAll('.', '-');
    const help = (helps && helps.filter(h => h.key === sKey)[0]) || null;

    return (
      <>
        {editableTranslate(translation_key, fallback)}
        {help && helpTextIcon(sKey, help.short_text, 'text-normal')}
      </>
    );
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <h4>
            {editableTranslate("global.menu.settings.interview")}
          </h4>
          <p className="section-description">
            {getLabel('interview.description', 'interview.description', 'You can enable or disable interviews for your employees.')}
          </p>

          <div className="settings-form settings-employee">
            <div className="mt-4">
              {error && (
                <div className="mt-3">
                  <ErrorAlert error={error} onClose={() => setError(null)} />
                </div>
              )}

              <table className="free-fields-table interview-settings-table" data-testid="interviews-table">
                <thead>
                  <tr>
                    <th className="text-center">{editableTranslate('interview.enable', 'Aktiv')}</th>
                    <th className="interview-settings-table__name">{editableTranslate('interview.interview_name', 'Gesprächsart')}</th>
                    <th>
                      {editableTranslate('interview.interview_due_date', 'Fälligkeitsdatum (optional)')}
                      <i className="bi bi-question-circle-fill ml-2" data-tooltip-id="interviewDueDate"></i>
                      <ReactTooltip id="interviewDueDate" place="top">
                        {editableTranslate('tooltips.interview.interview_due_date_tooltip', 'Dies ist optional. Wählen Sie ein Fälligkeitsdatum für das Vorstellungsgespräch, um einen Zeitplan festzulegen. Durch das Entfernen des Datums wird die Zeitleiste gelöscht')}
                      </ReactTooltip>
                    </th>
                    <th>{editableTranslate('interview.interview_min_characters', 'minimale Zeichenlänge')}</th>
                    <th className="text-center text-nowrap">
                      {editableTranslate('interview.interview_mandatory', 'Pflicht')}
                      <i className="bi bi-question-circle-fill ml-2" data-tooltip-id="interviewMandatory"></i>
                      <ReactTooltip id="interviewMandatory" place="top">
                        {editableTranslate('tooltips.interview.interview_mandatory_tooltip', 'Pflichtgespräche müssen abgeschlossen sein, bevor die Leistungskarte abgeschlossen werden kann. Freiwillige Gespräche blockieren den Abschluss nicht.')}
                      </ReactTooltip>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {
                    !interviews && <tr>
                      <td colSpan={5} className="text-center" data-testid="loading">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="sr-only">Wird geladen...</span>
                        </div>
                      </td>
                    </tr>
                  }
                  {
                    interviews && interviews.map((interview, i) => {
                      return <tr key={i}>
                        <td className="text-center">
                          <input type="hidden" {...register(`interview_settings.${i}.hash_id`)} />
                          <input type="checkbox" className="m-auto" {...register(`interview_settings.${i}.is_active`)} data-testid={`interview-setting-${i}-is-active`} />
                        </td>
                        <td>
                          <Field error={errors?.interview_settings?.[i]?.name?.message}>
                            <Input
                              type="text"
                              error={!!errors?.interview_settings?.[i]?.name}
                              {...register(`interview_settings.${i}.name`)}
                              disabled={!watchedSettings?.[i]?.is_active}
                              maxLength={255}
                            />
                          </Field>
                        </td>
                        <td>
                          <div className="interview-settings-table__date">
                            <DateInput
                              error={!!errors?.interview_settings?.[i]?.due_date}
                              {...register(`interview_settings.${i}.due_date`)}
                              disabled={!watchedSettings?.[i]?.is_active}
                              maxLength={12}
                            />
                            {errors?.interview_settings?.[i]?.due_date && (
                              <div className="peb-field__error">
                                {errors.interview_settings[i].due_date.message}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <Field className="interview-settings-table__length" error={errors?.interview_settings?.[i]?.min_length?.message}>
                            <Input
                              type="number"
                              number
                              error={!!errors?.interview_settings?.[i]?.min_length}
                              {...register(`interview_settings.${i}.min_length`)}
                              disabled={!watchedSettings?.[i]?.is_active}
                              min={0}
                              max={255}
                            />
                          </Field>
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="m-auto"
                            {...register(`interview_settings.${i}.is_mandatory`)}
                            disabled={!watchedSettings?.[i]?.is_active}
                            data-testid={`interview-setting-${interview.hash_id}-is-mandatory`}
                          />
                        </td>
                      </tr>
                    })
                  }
                </tbody>
              </table>

              {
                interviews && <>
                  <div className="mt-4">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="complaintEnable" {...register('complaint_enable')} data-testid="complaint-enable-checkbox" />
                      <label className="form-check-label" htmlFor="complaintEnable">
                        {getLabel('interview.complaint_enable', 'interview.complaint_enable', 'Enable complaint')}
                      </label>
                    </div>
                  </div>
                  <div className="settings-sticky-footer">
                    <hr className="mt-0" />
                    <Button variant="primary" onClick={handleSubmit(onSubmit)} loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>
                  </div>
                </>
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
